import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type {
  AvailabilityStatus,
  DeclaredHoliday,
  ExceptionReason,
  TimetableWeek,
  Weekday,
} from '@/types/timetable';
import { examsApi } from '../api/exams.api';
import { timetableApi } from '../api/timetable.api';
import type { AllocatedClass, TimetableSlot as TeacherTimetableSlot } from '../types/teacher';

import { allotmentLabel, instituteMinutesNow } from '../utils';
import { MyClassesCard } from './dashboard/MyClassesCard';
import { TimetableHeaderBar } from './timetable/TimetableHeaderBar';
import { TimetableSummaryCards } from './timetable/TimetableSummaryCards';
import { TimetableGrid } from './timetable/TimetableGrid';
import { examsByDate as groupExamsByDate } from '@/components/timetable/examPlacement';
import { TimetableFooter } from './timetable/TimetableFooter';
import { LockedNoticeModal } from './timetable/LockedNoticeModal';
import { REASON_BY_CATEGORY } from './timetable/timetableCopy';

export interface GridTimeSlot {
  timeStart: string;
  timeEnd: string;
  label: string;
  breakLabel: string | null;
  /** The period's own number, as the office set it — never its row position. */
  periodNumber: number;
}

export type { AvailabilityStatus };
export type LeaveCategory = 'Medical / Emergency' | 'Casual Leave' | 'Official Duty' | 'Other';

export interface InteractiveTimetableSlot extends TeacherTimetableSlot {
  dateISO: string;
  status: AvailabilityStatus;
  leaveCategory?: LeaveCategory;
  leaveReason?: string;
  isLeisure?: boolean;
  isBreak?: boolean;
}

export interface DayColumnHeader {
  dateISO: string;
  dayName: string;
  dateDisplay: string;
  yearStr: string;
  isPastDay: boolean;
  isToday: boolean;
  isFutureDay: boolean;
}

const CATEGORY_BY_REASON: Record<ExceptionReason, LeaveCategory> = {
  MEDICAL: 'Medical / Emergency',
  CASUAL: 'Casual Leave',
  OFFICIAL_DUTY: 'Official Duty',
  OTHER: 'Other',
};

const LEAVE_CATEGORY_OPTIONS: Option<LeaveCategory>[] = [
  { value: 'Medical / Emergency', label: 'Medical / Emergency Leave' },
  { value: 'Casual Leave', label: 'Casual Leave' },
  { value: 'Official Duty', label: 'Official Duty / Exam Invigilation' },
  { value: 'Other', label: 'Other Reason' },
];

const DAY_NAME: Record<Weekday, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatDateISO(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(d: Date): string {
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
}

function getDayName(d: Date): string {
  return DAY_NAMES[d.getDay()];
}

function parseTo24HourMinutes(timeStr: string): number {
  const [hStr, mStr] = timeStr.split(':');
  let hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);
  if (hours >= 1 && hours <= 5) {
    hours += 12;
  }
  return hours * 60 + minutes;
}

function getWeekRangeForDate(
  selectedDateISO: string,
  todayISO: string,
  workingDays: Weekday[],
): { startISO: string; endISO: string; days: DayColumnHeader[] } {
  let d = new Date(selectedDateISO + 'T00:00:00');
  if (Number.isNaN(d.getTime())) {
    d = new Date(todayISO + 'T00:00:00');
  }

  const dayOfWeek = d.getDay();
  const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const monDate = new Date(d);
  monDate.setDate(d.getDate() + diffToMon);

  const isWorking = new Set<string>(workingDays.map((day) => DAY_NAME[day]));
  const days: DayColumnHeader[] = [];

  for (let i = 0; i < 7; i++) {
    const cur = new Date(monDate);
    cur.setDate(monDate.getDate() + i);

    const dayName = getDayName(cur);
    if (workingDays.length > 0 && !isWorking.has(dayName)) continue;

    const dateISO = formatDateISO(cur);
    const isPastDay = Boolean(todayISO && dateISO < todayISO);
    const isToday = Boolean(todayISO && dateISO === todayISO);
    const isFutureDay = Boolean(todayISO && dateISO > todayISO);

    days.push({
      dateISO,
      dayName,
      dateDisplay: formatDateDisplay(cur),
      yearStr: String(cur.getFullYear()),
      isPastDay,
      isToday,
      isFutureDay,
    });
  }

  const startISO = days[0]?.dateISO ?? selectedDateISO;
  const endISO = days[days.length - 1]?.dateISO ?? selectedDateISO;

  return {
    startISO,
    endISO,
    days,
  };
}

interface TeacherTimetableSectionProps {
  allocatedClasses?: AllocatedClass[];
  selectedClassId?: string;
  onSelectClass?: (id: string) => void;
}

export function TeacherTimetableSection({
  allocatedClasses = [],
  selectedClassId,
  onSelectClass,
}: TeacherTimetableSectionProps) {
  // 1. Fetch current server day in IST
  const { data: anchor } = useApiQuery(() => timetableApi.day(), []);
  const todayISO = anchor?.date ?? '';

  // Calendar Date Filter
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedDayISO, setSelectedDayISO] = useState<string>('');
  // The allotment the grid is narrowed to. Kept here rather than in the
  // dashboard's class selection: the week is always the teacher's whole
  // timetable, so "all my classes" has to be a choice the picker can offer.
  const [classFilterId, setClassFilterId] = useState<string>('');
  const filteredClass = allocatedClasses.find((c) => c.id === classFilterId) ?? null;

  useEffect(() => {
    if (todayISO && !selectedDate) {
      setSelectedDate(todayISO);
    }
  }, [todayISO, selectedDate]);

  // 2. Fetch weekly timetable dynamically from API
  const {
    data: week,
    isLoading: isLoadingWeek,
    error: weekError,
    refetch: refetchWeek,
  } = useApiQuery<TimetableWeek | null>(
    () => (selectedDate ? timetableApi.week(selectedDate) : Promise.resolve(null)),
    [selectedDate],
  );

  // The office's exam timetable, from the same list the Exams page reads. Not
  // essential to the week: if it fails the lessons still show, with a note.
  const {
    data: exams,
    error: examsError,
    refetch: refetchExams,
  } = useApiQuery(() => examsApi.list(), []);

  // 3. Compute week boundary using institute working days
  const weekInfo = useMemo(() => {
    const activeDate = selectedDate || todayISO || formatDateISO(new Date());
    return getWeekRangeForDate(activeDate, todayISO, week?.workingDays ?? []);
  }, [selectedDate, todayISO, week?.workingDays]);

  const daysHeader = weekInfo.days;

  /**
   * Periods per section in the week on screen, for the allotment list. Counted
   * from this teacher's own slots — the same week the grid draws — so the list
   * and the grid can never disagree.
   */
  const periodsBySection = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const day of week?.days ?? []) {
      for (const slot of day.slots) {
        counts[slot.classSectionUuid] = (counts[slot.classSectionUuid] ?? 0) + 1;
      }
    }
    return counts;
  }, [week]);

  // The day picked opens selected; otherwise today, otherwise the week's first.
  useEffect(() => {
    if (daysHeader.length > 0) {
      const pickedMatch = daysHeader.find((d) => d.dateISO === selectedDate);
      const todayMatch = daysHeader.find((d) => d.isToday);
      setSelectedDayISO((pickedMatch ?? todayMatch ?? daysHeader[0]).dateISO);
    }
  }, [daysHeader, selectedDate]);

  function handlePrevWeek() {
    const cur = new Date((selectedDate || todayISO || formatDateISO(new Date())) + 'T00:00:00');
    cur.setDate(cur.getDate() - 7);
    setSelectedDate(formatDateISO(cur));
  }

  function handleNextWeek() {
    const cur = new Date((selectedDate || todayISO || formatDateISO(new Date())) + 'T00:00:00');
    cur.setDate(cur.getDate() + 7);
    setSelectedDate(formatDateISO(cur));
  }

  function handleThisWeek() {
    setSelectedDate(todayISO || formatDateISO(new Date()));
  }

  const weekRangeText = useMemo(() => {
    if (daysHeader.length === 0) return '';
    const first = daysHeader[0];
    const last = daysHeader[daysHeader.length - 1];
    return `${first.dateDisplay} – ${last.dateDisplay}, ${last.yearStr}`;
  }, [daysHeader]);

  // 4. Dynamic period rows mapped directly from API
  const timeSlots: GridTimeSlot[] = useMemo(() => {
    if (week?.periods && week.periods.length > 0) {
      return week.periods.map((period) => ({
        timeStart: period.startTime,
        timeEnd: period.endTime,
        label: `${period.startTime} - ${period.endTime}`,
        breakLabel: period.kind === 'BREAK' ? (period.label ?? 'Break') : null,
        periodNumber: period.periodNumber,
      }));
    }
    return [];
  }, [week]);

  // Only the papers in a subject this teacher is allotted, in that section.
  // The list holds every paper of every section they teach in, and another
  // subject's paper there is another teacher's to set and sit — on their week
  // it read as if it were theirs. The Exams page keeps the wider list.
  const examsByDate = useMemo(() => {
    const ownSubjects = new Set(
      allocatedClasses.flatMap((allotment) =>
        allotment.subjectUuid ? [`${allotment.classSectionUuid}:${allotment.subjectUuid}`] : [],
      ),
    );
    const ownPapers = (exams ?? []).filter((exam) =>
      ownSubjects.has(`${exam.classSection.uuid}:${exam.subject.uuid}`),
    );
    return groupExamsByDate(ownPapers, daysHeader.map((day) => day.dateISO));
  }, [exams, allocatedClasses, daysHeader]);

  // Declared holidays in this week
  const holidayByDate = useMemo(() => {
    const byDate = new Map<string, DeclaredHoliday>();
    for (const day of week?.days ?? []) {
      if (day.holiday) byDate.set(day.date, day.holiday);
    }
    return byDate;
  }, [week]);

  // 5. Dynamic slot map directly from API
  const slotsByDate = useMemo(() => {
    const byDate: Record<string, InteractiveTimetableSlot[]> = {};
    if (!week) return byDate;

    for (const day of daysHeader) {
      if (holidayByDate.has(day.dateISO)) {
        byDate[day.dateISO] = [];
        continue;
      }

      const serverDay = week.days?.find((d) => d.date === day.dateISO);
      const serverSlots = serverDay?.slots ?? [];
      const mine = new Map(serverSlots.map((s) => [s.period.uuid, s]));

      byDate[day.dateISO] = week.periods.map((period, index) => {
        const slot = mine.get(period.uuid);
        const time = `${period.startTime} - ${period.endTime}`;
        const isBreak = period.kind === 'BREAK';

        if (!slot) {
          return {
            id: `${isBreak ? 'break' : 'free'}-${day.dateISO}-${period.uuid}`,
            dateISO: day.dateISO,
            day: day.dayName as TeacherTimetableSlot['day'],
            period: period.periodNumber || index + 1,
            time,
            subject: isBreak ? (period.label ?? 'Break') : 'Free',
            className: '',
            section: '',
            roomNo: '',
            status: 'AVAILABLE' as AvailabilityStatus,
            isLeisure: true,
            isBreak,
          };
        }

        return {
          id: slot.uuid,
          dateISO: day.dateISO,
          day: day.dayName as TeacherTimetableSlot['day'],
          period: period.periodNumber,
          time,
          subject: slot.subject.name,
          className: slot.displayName,
          section: '',
          roomNo: slot.roomName ?? '',
          status: slot.availability.status,
          leaveCategory: slot.availability.reason
            ? CATEGORY_BY_REASON[slot.availability.reason]
            : undefined,
          leaveReason: slot.availability.note ?? undefined,
        };
      });
    }

    return byDate;
  }, [daysHeader, holidayByDate, week]);

  /**
   * The grid's copy of the week, narrowed to the picked allotment. A lesson in
   * another class is drawn as an empty period rather than removed, so the
   * period rows still line up. The summary cards keep counting the whole week
   * — they describe the teacher, not the class on screen.
   */
  const gridSlotsByDate = useMemo(() => {
    if (!filteredClass || !week) return slotsByDate;

    const shown = new Set<string>();
    for (const day of week.days ?? []) {
      for (const slot of day.slots) {
        const sameSection = slot.classSectionUuid === filteredClass.classSectionUuid;
        // A class-teacher-only allotment has no subject, and covers every
        // lesson this teacher gives that section.
        const sameSubject =
          filteredClass.subjectUuid === null || slot.subject.uuid === filteredClass.subjectUuid;
        if (sameSection && sameSubject) shown.add(slot.uuid);
      }
    }

    const byDate: Record<string, InteractiveTimetableSlot[]> = {};
    for (const [dateISO, slots] of Object.entries(slotsByDate)) {
      byDate[dateISO] = slots.map((slot) =>
        slot.isLeisure || shown.has(slot.id) ? slot : { ...slot, isLeisure: true },
      );
    }
    return byDate;
  }, [filteredClass, slotsByDate, week]);

  // 6. Dynamic KPI card metrics computed strictly from API slots
  const allSlotsInRange: InteractiveTimetableSlot[] = useMemo(() => {
    return daysHeader.flatMap((d) => slotsByDate[d.dateISO] ?? []);
  }, [daysHeader, slotsByDate]);

  const assignedClassSlots = useMemo(() => {
    return allSlotsInRange.filter((s) => !s.isLeisure && !s.isBreak);
  }, [allSlotsInRange]);

  const dynamicTotalSubjects = useMemo(() => {
    const subjects = new Set<string>();
    for (const slot of assignedClassSlots) {
      if (slot.subject && slot.subject !== 'Free') {
        subjects.add(slot.subject);
      }
    }
    return subjects.size;
  }, [assignedClassSlots]);

  const dynamicTotalPeriods = week?.summary?.periodsThisWeek ?? assignedClassSlots.length;

  const dynamicAttendance = useMemo(() => {
    if (!week) return '—';
    if (week.summary) {
      const total = week.summary.periodsThisWeek;
      const unavail = week.summary.unavailable;
      if (total === 0) return '—';
      return `${Math.round(((total - unavail) / total) * 100)}%`;
    }
    if (assignedClassSlots.length === 0) return '—';
    const available = assignedClassSlots.filter((s) => s.status === 'AVAILABLE').length;
    return `${Math.round((available / assignedClassSlots.length) * 100)}%`;
  }, [week, assignedClassSlots]);

  function isPeriodCompleted(dateISO: string, timeEnd: string): boolean {
    if (!todayISO) return false;
    if (dateISO < todayISO) return true;
    if (dateISO > todayISO) return false;

    // The school's clock, not the device's: the bell schedule is in IST, and a
    // phone in another zone would lock or unlock periods hours out.
    return parseTo24HourMinutes(timeEnd) <= instituteMinutesNow();
  }

  // Modals state
  const [editingSlot, setEditingSlot] = useState<InteractiveTimetableSlot | null>(null);
  const [modalStatus, setModalStatus] = useState<AvailabilityStatus>('AVAILABLE');
  const [modalCategory, setModalCategory] = useState<LeaveCategory>('Medical / Emergency');
  const [modalReason, setModalReason] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const [lockedNoticeSlot, setLockedNoticeSlot] = useState<{
    slot: InteractiveTimetableSlot;
    reason: string;
  } | null>(null);

  function handleOpenSlotModal(slot: InteractiveTimetableSlot, dayObj: DayColumnHeader) {
    if (slot.isLeisure || slot.isBreak) return;

    const completed = isPeriodCompleted(dayObj.dateISO, slot.time.split(' - ')[1] || '17:00');

    if (dayObj.isPastDay || completed) {
      const lockReason = dayObj.isPastDay
        ? `This class was scheduled on ${dayObj.dayName}, ${dayObj.dateDisplay} ${dayObj.yearStr}, which is in the past. Past records are locked for audit compliance.`
        : `This period (${slot.time}) has already concluded today. Completed periods cannot be edited.`;

      setLockedNoticeSlot({ slot, reason: lockReason });
      return;
    }

    setEditingSlot(slot);
    setModalStatus(slot.status);
    setModalCategory(slot.leaveCategory || 'Medical / Emergency');
    setModalReason(slot.leaveReason || '');
  }

  async function handleSaveSlotAvailability() {
    if (!editingSlot) return;
    setIsSaving(true);

    try {
      await timetableApi.setAvailability(editingSlot.id, {
        date: editingSlot.dateISO,
        status: modalStatus,
        ...(modalStatus === 'UNAVAILABLE' && {
          reason: REASON_BY_CATEGORY[modalCategory],
          ...(modalReason.trim() && { note: modalReason.trim() }),
        }),
      });

      toast.success(
        modalStatus === 'AVAILABLE'
          ? 'Marked as available'
          : `Marked away for ${editingSlot.subject}`,
        `${editingSlot.day} · ${editingSlot.time}`,
      );
      setEditingSlot(null);
      refetchWeek();
    } catch (caught) {
      toast.error(
        'Could not update availability',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const isCurrentWeek =
    Boolean(todayISO && weekInfo.startISO <= todayISO && todayISO <= weekInfo.endISO);

  // Named by what the sheet holds: the whole week is "My timetable", not
  // whichever class the dashboard last had selected.
  const printTitle = filteredClass ? allotmentLabel(filteredClass) : 'My timetable';

  const handleDownload = () => {
    const originalTitle = document.title;
    const weekLabel = weekRangeText ? ` (${weekRangeText})` : '';
    document.title = `${printTitle}${weekLabel}`.replace(/[\\/:*?"<>|]/g, '-');
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  return (
    <div className="flex flex-col gap-6 print:block print:gap-0">
      {/* 1. Summary Metrics Cards (100% dynamic from API data) */}
      <div className="print:hidden">
        <TimetableSummaryCards
          totalSubjects={dynamicTotalSubjects}
          totalPeriods={dynamicTotalPeriods}
          // Not a guessed six before the week arrives: the institute's own
          // working days decide it.
          daysInWeek={week ? daysHeader.length : '—'}
          attendance={dynamicAttendance}
        />
      </div>


      {/* 2. Header Bar with Class and Week Controls */}
      <div className="print:hidden">
        <TimetableHeaderBar
          allocatedClasses={allocatedClasses}
          classFilterId={classFilterId}
          onClassFilterChange={setClassFilterId}
          weekRangeText={weekRangeText}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          onThisWeek={handleThisWeek}
          isCurrentWeek={isCurrentWeek}
          pickedDate={selectedDate}
          onPickDate={setSelectedDate}
        />
      </div>

      {/* Print-only Timetable Header Banner */}
      <div className="hidden print:block mb-2 pb-1.5 border-b border-line w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-ink">
              {printTitle}
            </h1>
            <p className="text-[11px] text-ink-3 font-medium mt-0.5">
              Week: {weekRangeText}
            </p>
          </div>
          <div className="text-right text-xs text-ink-3 font-medium">
            {daysHeader[0]?.dateDisplay ? `Week of ${daysHeader[0].dateDisplay} ${daysHeader[0].yearStr}` : ''}
          </div>
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoadingWeek && (
        <div className="card p-6 rounded-[20px] bg-surface border border-line shadow-2xs">
          <SkeletonLines count={8} />
        </div>
      )}

      {/* Error Message */}
      {!isLoadingWeek && weekError && (
        <div className="card flex flex-col items-start gap-3 p-6 rounded-[20px] bg-surface border border-line shadow-2xs">
          <Alert tone="critical" title="Could not load timetable">
            {weekError.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetchWeek}>
            Try again
          </Button>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingWeek && !weekError && timeSlots.length === 0 && (
        <div className="card rounded-[20px] bg-surface border border-line shadow-2xs">
          <EmptyState
            title="No timetable periods configured"
            description="Your institute has not yet configured timetable periods for this week."
          />
        </div>
      )}

      {examsError && !isLoadingWeek && !weekError && (
        <div className="flex flex-col items-start gap-3 print:hidden">
          <Alert tone="warning" title="Exam dates could not be loaded">
            Your lessons are shown, but papers on the exam timetable are missing from this week.
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetchExams}>
            Try again
          </Button>
        </div>
      )}

      {/* 3. Timetable Grid Matrix */}
      {!isLoadingWeek && !weekError && timeSlots.length > 0 && (
        <TimetableGrid
          daysHeader={daysHeader}
          timeSlots={timeSlots}
          slotsByDate={gridSlotsByDate}
          holidayByDate={holidayByDate}
          examsByDate={examsByDate}
          selectedDayISO={selectedDayISO}
          onSelectDayISO={setSelectedDayISO}
          selectedStatusFilter="ALL"
          isPeriodCompleted={isPeriodCompleted}
          onOpenSlotModal={handleOpenSlotModal}
        />
      )}

      {/* 4. My Allocated Classes — shown below the timetable grid */}
      {allocatedClasses.length > 0 && (
        <div id="allocated-classes" className="print:hidden">
          <MyClassesCard
            title="My allocated classes"
            classes={allocatedClasses}
            periodsBySection={periodsBySection}
            selectedClassId={selectedClassId}
            onSelectClass={onSelectClass}
            showAll
          />
        </div>
      )}

      {/* 5. Footer Note & Download Button */}
      <TimetableFooter
        weekOf={
          daysHeader[0]?.dateDisplay
            ? `${daysHeader[0].dateDisplay} ${daysHeader[0].yearStr}`
            : ''
        }
        onDownload={handleDownload}
      />

      {/* Locked Notice Modal */}
      {lockedNoticeSlot && (
        <LockedNoticeModal
          slot={lockedNoticeSlot.slot}
          reason={lockedNoticeSlot.reason}
          onClose={() => setLockedNoticeSlot(null)}
        />
      )}

      {/* Availability / Sudden Leave Edit Modal */}
      {editingSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-up">
          <div className="w-full max-w-md overflow-hidden rounded-[22px] bg-surface border border-line shadow-2xl flex flex-col text-xs">
            <div className="flex items-center justify-between border-b border-line bg-surface-2 px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-ink">Notify Class Availability</h3>
                <p className="text-xs text-ink-3 font-medium mt-0.5">
                  {editingSlot.day} ({editingSlot.time}) · {editingSlot.subject}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingSlot(null)}
                className="rounded-lg p-1.5 text-ink-3 hover:bg-surface-3 hover:text-ink transition-colors cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-bold text-ink mb-1">
                  Availability Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setModalStatus('AVAILABLE')}
                    className={`py-2 px-3 rounded-lg border font-bold text-xs transition-all cursor-pointer ${
                      modalStatus === 'AVAILABLE'
                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                        : 'border-line bg-surface text-ink-3 hover:text-ink'
                    }`}
                  >
                    Available
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalStatus('UNAVAILABLE')}
                    className={`py-2 px-3 rounded-lg border font-bold text-xs transition-all cursor-pointer ${
                      modalStatus === 'UNAVAILABLE'
                        ? 'border-rose-500 bg-rose-500/15 text-rose-600 dark:text-rose-400'
                        : 'border-line bg-surface text-ink-3 hover:text-ink'
                    }`}
                  >
                    Not Available
                  </button>
                </div>
              </div>

              {modalStatus === 'UNAVAILABLE' && (
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-xs font-bold text-ink mb-1">
                      Reason for Absence
                    </label>
                    <Select
                      label="Reason for Absence"
                      hideLabel
                      options={LEAVE_CATEGORY_OPTIONS}
                      value={modalCategory}
                      onChange={(e) => setModalCategory(e.target.value as LeaveCategory)}
                      className="w-full text-xs font-semibold rounded-field"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-ink mb-1">
                      Additional Note (optional)
                    </label>
                    <textarea
                      rows={3}
                      value={modalReason}
                      onChange={(e) => setModalReason(e.target.value)}
                      placeholder="e.g. Doctor appointment, sudden emergency..."
                      className="w-full p-2.5 bg-surface-2 border border-line rounded-field text-xs text-ink placeholder:text-ink-3 focus:outline-none focus:border-indigo-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-2 px-5 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingSlot(null)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveSlotAvailability}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Availability'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
